import { Badge, IconHighlight } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { SectionLabel, DetailSections } from '../shared/index.js';
import { confidencePercent, formatDate } from '../../../utils/detailFormat.js';
import { ArrowUpCircle, FileStack } from 'lucide-react';

function riskBadgeVariant(riskLevel) {
  switch (String(riskLevel ?? '').toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'neutral';
  }
}

/**
 * Gov Promotion pipeline detail for interim `proposal` events (CT-5091).
 *
 * Visual language follows the aims-htl Gov Promotion / truth-promotion cards:
 * hero context, side-by-side CURRENT/PROPOSED comparison (EVT-012 pattern),
 * signal badges, and rationale — adapted to `POST /v1/my-work/proposal/list`
 * fields (no preview endpoint yet).
 */
function ProposalBody({ event }) {
  const { t } = useTranslation('work-queues-app');
  const detail = event.detail ?? {};

  const current = detail.currentStatement ?? null;
  const proposed = detail.proposedStatement ?? event.situation ?? null;
  const rationale = event.description && event.description !== proposed ? event.description : null;
  const confidence = confidencePercent(detail.confidence);
  const evidenceCount = detail.evidenceCount ?? event.commentsCount ?? 0;
  const kbName = detail.knowledgebaseName ?? event.refLabel ?? null;
  const updatedAt = formatDate(detail.updatedAt);

  const contextHero =
    kbName || event.title ? (
      <div
        key="context"
        className="rounded-xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/10 via-violet-500/5 to-transparent p-4"
      >
        <div className="flex items-start gap-3">
          <IconHighlight bgColor="Purple" iconColor="Purple" size="medium" icon={ArrowUpCircle} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-content">{t('proposal.promotionCandidate')}</p>
              <Badge variant="purple" size="small">
                {t('eventType.proposal')}
              </Badge>
            </div>
            {event.title && (
              <p className="mt-1 break-words text-[13px] font-medium leading-snug text-content">{event.title}</p>
            )}
            <dl className="mt-2.5 flex flex-wrap gap-x-6 gap-y-1 text-[11px]">
              {kbName && (
                <div>
                  <dt className="text-content-faint">{t('proposal.knowledgeBase')}</dt>
                  <dd className="font-medium text-content-secondary">{kbName}</dd>
                </div>
              )}
              {updatedAt && (
                <div>
                  <dt className="text-content-faint">{t('proposal.lastUpdated')}</dt>
                  <dd className="font-medium text-content-secondary">{updatedAt}</dd>
                </div>
              )}
              {event.studio && (
                <div>
                  <dt className="text-content-faint">{t('proposal.studio')}</dt>
                  <dd className="font-medium text-content-secondary">{event.studio}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    ) : null;

  const comparison = proposed ? (
    <div key="comparison">
      <SectionLabel>{t('proposal.comparison')}</SectionLabel>
      <div className="flex flex-col gap-3 sm:flex-row">
        {current && (
          <div className="flex-1 rounded-lg border border-edge bg-surface-muted p-4">
            <span className="mb-2.5 inline-block rounded bg-sky-500/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-sky-400">
              {t('proposal.currentValue')}
            </span>
            <p className="break-words text-[13px] leading-relaxed text-content-secondary">{current}</p>
          </div>
        )}
        <div className="flex-1 rounded-lg border border-violet-500/30 bg-violet-500/5 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <span className="mb-2.5 inline-block rounded bg-violet-500/20 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-300">
            {t('proposal.proposedValue')}
          </span>
          <p className="break-words text-[13px] font-semibold leading-relaxed text-content">{proposed}</p>
        </div>
      </div>
    </div>
  ) : null;

  const signals =
    confidence || detail.riskLevel || evidenceCount > 0 ? (
      <div key="signals">
        <SectionLabel>{t('proposal.signals')}</SectionLabel>
        <div className="flex flex-wrap items-center gap-2">
          {confidence && (
            <Badge variant="informative" size="medium">
              {t('proposal.confidence')}: {confidence}
            </Badge>
          )}
          {detail.riskLevel && (
            <Badge variant={riskBadgeVariant(detail.riskLevel)} size="medium">
              {t('proposal.risk')}: {detail.riskLevel}
            </Badge>
          )}
          {evidenceCount > 0 && (
            <Badge variant="neutral" size="medium" icon={FileStack}>
              {t('proposal.evidenceCount')}: {evidenceCount}
            </Badge>
          )}
        </div>
      </div>
    ) : null;

  const rationaleBlock = rationale ? (
    <div key="rationale">
      <SectionLabel>{t('proposal.rationale')}</SectionLabel>
      <p className="rounded-lg border border-edge bg-surface-muted px-3 py-2.5 text-[13px] leading-relaxed text-content-secondary">
        {rationale}
      </p>
    </div>
  ) : null;

  const sections = [contextHero, comparison, signals, rationaleBlock].filter(Boolean);

  if (!sections.length) {
    return (
      <p className="text-sm leading-relaxed text-content-secondary">{event.situation ?? event.description}</p>
    );
  }

  return <DetailSections sections={sections} />;
}

export default ProposalBody;
